import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormMode } from 'src/app/controls/common/form-mode';
import { ContextOfUse } from 'src/app/models/RestApiCaller/Enums/context-of-use';
import { ApiConfigurationState } from '../models/api-configuration-state';

@Component({
    selector: 'app-api-configuration-general',
    imports: [CommonModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatSelectModule],
    templateUrl: './api-configuration-general.component.html',
    styleUrl: './api-configuration-general.component.scss'
})
export class ApiConfigurationGeneralComponent implements OnInit {
    /**
     * Etat de la configuration d'API
     */
    protected readonly apiConfigurationState = inject(ApiConfigurationState);

    /**
     * Liste des enums utilisés dans le template.
     */
    public FormMode = FormMode;

    /**
     * Liste des contextes d'utilisation et leur libellé.
     */
    public contextOfUses: Map<ContextOfUse, string> = new Map<ContextOfUse, string>();

    /**
     * Formulaire contenant les informations générales de la configuration d'API.
     */
    public apiConfigurationGeneralForm: FormGroup;

    /**
     * Constructeur.
     */
    constructor() {
        this.apiConfigurationGeneralForm = new FormGroup({
            name: new FormControl('', Validators.required),
            description: new FormControl(''),
            contextOfUse: new FormControl('', Validators.required),
            timeout: new FormControl(''),
        });

        this.contextOfUses.set(ContextOfUse.WorkflowAction, 'Action de workflow');
        this.contextOfUses.set(ContextOfUse.AutocompletionDatasource, "Source de données d'autocompletion");

        effect(() => {
            if (this.apiConfigurationState.$mode() === FormMode.Reading) {
                this.apiConfigurationGeneralForm.disable();
            } else {
                this.apiConfigurationGeneralForm.enable();
            }
        });
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit(): void {
        this.fillForm();
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Annuler'.
     */
    public onCancelClick() {
        this.fillForm();
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Enregistrer'.
     */
    public onSaveClick() {
        if (this.apiConfigurationGeneralForm.valid) {
            let apiConfiguration = this.apiConfigurationState.$apiConfiguration().createCopy();
            apiConfiguration.name = this.apiConfigurationGeneralForm.get('name').value;
            apiConfiguration.description = this.apiConfigurationGeneralForm.get('description').value;
            apiConfiguration.contextOfUse = this.apiConfigurationGeneralForm.get('contextOfUse').value;
            apiConfiguration.timeout = this.apiConfigurationGeneralForm.get('timeout').value;

            this.apiConfigurationState.$apiConfiguration.set(apiConfiguration);
        }
    }

    /**
     * Remplit les champs du formulaire avec les données de la configuration d'API.
     */
    private fillForm() {
        this.apiConfigurationGeneralForm.get('name').setValue(this.apiConfigurationState.$apiConfiguration().name);
        this.apiConfigurationGeneralForm.get('description').setValue(this.apiConfigurationState.$apiConfiguration().description);
        this.apiConfigurationGeneralForm.get('contextOfUse').setValue(this.apiConfigurationState.$apiConfiguration().contextOfUse);
        this.apiConfigurationGeneralForm.get('timeout').setValue(this.apiConfigurationState.$apiConfiguration().timeout);
    }
}
